import React, { useEffect, useState } from 'react';
import { Modal,  Button, Row, Col, Checkbox,Select, message  } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { DataIndexTableColumnModalContainer } from '../data-index-table-modal.styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { DataAOI } from '../../../../types/interfaces';
import { exportLAS, getDataForLASExport, getTargetEPSGList } from '../../../../common/lasExportUtils';
import MapComponent from '../../../common/MapComponent';
import { MapData } from '../../../../types/mapInterfaces';
import { createMapPolygonFromDataAOI } from '../../../../common/MapComponentUtils';
import { SearchableTable } from './SearchableTable';
import { useAPIOnce } from './useAPIOnce';
import { EPSG_INdexData, compoundCRS, horizontalCRSList, verticalCRSList } from '../../../../common/EPSGIndexUtils';
import { LASDataClassification, getVisibleClasses } from '../../../../common/LASDataClassification';
import { getIdToken } from '../../../userManager/userManagementUtils';
const CheckboxGroup = Checkbox.Group;


interface ChildComponentProps {
    visible: boolean;
    onClose: () => void;
    record: DataAOI;
}

export const AoisLasExport: React.FC<ChildComponentProps> = ({ visible, onClose, record }) => {

    const [selectedClasses, setSelectedClasses] = useState<any[]>([]);
    const [visibleData, setVisibleData] = useState<any[]>([]);
    const [horizontalCRSModal, setHorizontalCRSModal] = useState<boolean>(false);
    const [verticalCRSModal, setVerticalCRSModal] = useState<boolean>(false);
    const activeProjection = window.activeProjection?.getCRS() || "";
    const [selectedCRS, setSelectedCRS] = useState<string>(activeProjection);
    const [exportLoading, setExportLoading] = useState<boolean>(false);
    
    const [selectedHCRS, setSelectedHCRS] = useState<EPSG_INdexData>({
        code: window.activeProjection?.getHorizontalCRS() || ""
    } as EPSG_INdexData);
    const [selectedVCRS, setSelectedVCRS] = useState<EPSG_INdexData>({
        code: window.activeProjection?.getVerticalCRS()  || ""
    } as EPSG_INdexData);

    let polygon = createMapPolygonFromDataAOI(record);
    const mapData: MapData = {
        polygons: [polygon],
    }

    var modalData = getDataForLASExport(record);

    // Get target EPSG data
    const targetEPSGList = getTargetEPSGList();

    useEffect(() => {
        const visibleData: any[] = [];
        Object.keys(modalData.visibleData).forEach((data, dataIndex) => (
            modalData.visibleData[data].forEach((item: any, index: number) => (
                visibleData.push({
                    owner: data,
                    name: item.name,
                    data_id: item.data_id,
                    isSelected : true,
                    projection : item.projection
                })
            ))
        ))
        setVisibleData(visibleData);
        const selectedItems = getVisibleClasses()
        // Get visible classes
        const visiblePotreeClass = LASDataClassification.filter((item :any,index:number)=> {
            let isSelected = selectedItems.find((item2: any) => item2.value === item.value)
                return isSelected
            })
        setSelectedClasses(visiblePotreeClass);
    }, []);

    useEffect(() => {
        
        let newCRS = "EPSG:" +  selectedHCRS?.code;
        let kind = selectedHCRS?.kind;

        if (compoundCRS.includes(kind)) {
            setSelectedCRS(newCRS);
            setSelectedVCRS({code: ""} as EPSG_INdexData);
            return;
        }

        if (selectedVCRS?.code !== "") {
            newCRS = `${newCRS}+${selectedVCRS.code}`;
        }
        setSelectedCRS(newCRS);
    },[selectedHCRS, selectedVCRS])

    // For classification checkboxes
    const indeterminate = selectedClasses.length > 0 && selectedClasses.length < LASDataClassification.length;
    const onChange = (list: any[]) => {
        let newList = LASDataClassification.filter((item :any,index:number)=> {
            let isSelected = list.find((item2: any) => item2 === item.label)
            return isSelected
        })
        setSelectedClasses(newList);

    };
    const onChangeVisibleData = (e: CheckboxChangeEvent) => {
        const checked = e.target.checked;
        const data = visibleData[e.target.value]
        data.isSelected = checked
        setVisibleData([...visibleData])
    }
    const onCheckAllChange = (e: CheckboxChangeEvent) => {
        setSelectedClasses(e.target.checked ? LASDataClassification : []);
    };

    // For target EPSG dropdown
    // Filter `option.label` match the user type `input`
    const filterOption = (input: string, option?: { label: string; value: string }) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

    const exportLas = async () => {
        setExportLoading(true)
        const classificationIndices = selectedClasses.map((item, index) => item.value);

        const data_ids : any = {}
        visibleData.forEach((item, index) => {
            if (!item.isSelected) {
                setExportLoading(false)
                return
            }
            if (data_ids[item.owner] === undefined) {
                data_ids[item.owner] = []
            }
            data_ids[item.owner].push(item.data_id)
        })
        if(Object.keys(data_ids).length === 0){
            alert("Please select at least one data to export")
            setExportLoading(false)
            return
        }
       
        try {
            const idToken = await getIdToken(); 
            const payload = {
                download_name: modalData.download_name,
                polygon: modalData.polygon,
                data_ids: data_ids,
                classification: classificationIndices.length > 0 ? classificationIndices : -1,
                process: "download",
                token: idToken,
                srs_override: selectedCRS,
            };
            await exportLAS(payload);
            message.success("Export initiated.");
        } catch (error) {
            message.error("Failed to get token or export data");
        } finally {
            setExportLoading(false);
        }
    }

    // Move Potree map to modal
    const mapParent = React.useRef<HTMLDivElement>(null);
    const isCompoundCRS = compoundCRS.includes(selectedHCRS?.kind);
    return (
    <>
        <Modal
            style={{ top: 20 }}
            width={650}
            title={<span style={{ color: '#fff', fontSize: '20px' }}>Download LAS</span>}
            centered
            open={visible}
            onCancel={onClose}
            footer={null}
            className='preventDrag process_modal'
        >
            <DataIndexTableColumnModalContainer>
                <div className='modal-top-header'>
                    <Row>
                        <Col xs={24} sm={12} md={12} lg={16} xl={14}>
                            <table className='lasData_table'>
                                <tbody>
                                    <tr>
                                        <td className="lasData_label">AOI Name:</td>
                                        <td>{modalData.download_name}</td>
                                    </tr>
                                    <tr>
                                        <td className="lasData_label">Site(Area):</td>
                                        <td>{modalData.area}</td>
                                    </tr>
                                    <tr>
                                        <td className="lasData_label">Current EPSG:</td>
                                        <td>{modalData.epsg}</td>
                                    </tr>
                                </tbody>

                            </table>
                        </Col>
                        <Col xs={24} sm={12} md={12} lg={8} xl={10}>
                            <div className='lasData_map' ref={mapParent}>
                                <MapComponent data={mapData}/>
                            </div>
                        </Col>
                    </Row>
                    <div>
                        {(visibleData && visibleData.length > 0) && (
                            <div>
                                <h5 className='table_title'>Input Data</h5>
                                <div className='inputData_table'>
                                    <TableContainer component={Paper} className='process_modal_table'>
                                        <Table aria-label="Input Data Table" size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={{'width' : '50px'}}></TableCell>
                                                    <TableCell>Owner</TableCell>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Projection</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody >
                                                {visibleData.map((item: any, index: number) => (
                                                    <TableRow key={index}>
                                                        <TableCell>
                                                            <Checkbox className='checkbox_table' checked={item.isSelected} value={index} onChange={onChangeVisibleData} />
                                                        </TableCell>
                                                        <TableCell>{item.owner}</TableCell>
                                                        <TableCell>{item.name}</TableCell>
                                                        <TableCell>{item.projection}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>
                            </div>
                        )}
                        <div className="visible_classes_block">
                            <h5 className='table_title'>Visible Classes</h5>
                            <div className='inlineBox'>
                                {/* <div className='flex-box'> */}
                                    <Checkbox className='checkbox_table' indeterminate={indeterminate} onChange={onCheckAllChange} checked={LASDataClassification.length === selectedClasses.length}>
                                    All
                                </Checkbox>
                                    <CheckboxGroup className='checkbox_table' options={LASDataClassification.map(e => e.label)} value={selectedClasses.map(e => e.label)} onChange={onChange} />
                                {/* </div> */}

                            </div>
                        </div>
                        <div className="visible_classes_block option_block">
                            <h5 className='table_title'>Options</h5>
                            <div className='flex-box'>
                                <p className="lasData_label">Target EPSG:</p>
                                <p className="lasData_label_text">{selectedCRS}</p>
                            </div>
                            <div className='flex-box'>
                                <p className="lasData_label">Horizontal:</p>
                                <p className="lasData_label_text">{selectedHCRS.code}</p>
                                <div className='select_button'>
                                    <Button type="primary" className='data-export' onClick={() => setHorizontalCRSModal(true)}>Change</Button>
                                </div>                                
                            </div>
                            <div className='flex-box'>
                                <p className="lasData_label">Vertical:</p>
                                <p className="lasData_label_text">{isCompoundCRS ? "-": selectedVCRS.code}</p>
                                <div className='select_button'>
                                    <Button disabled={isCompoundCRS} type="primary" className='data-export' onClick={() => setVerticalCRSModal(true)}>Change</Button>
                                </div>
                            </div>
                        </div>
                        <div className="visible_classes_block option-box">
                            <div className='flex-box'>
                                <Checkbox className='checkbox_table'>Compression</Checkbox>
                                <Checkbox className='checkbox_table'>Tile</Checkbox>
                            </div>
                        </div>
                        <div className="visible_classes_block">
                            <table className='lasData_table'>
                                <tbody>
                                    <tr>
                                        <td className="lasData_label">Est Data Size:</td>
                                        <td>-</td>
                                    </tr>
                                    <tr>
                                        <td className="lasData_label">Est Data Cost:</td>
                                        <td>-</td>
                                    </tr>
                                </tbody>
                            </table>

                        </div>
                        <div className="visible_classes_block align-right modal-footer">
                            <Button type="primary" className='data-export' loading={exportLoading} onClick={exportLas}>Export</Button>
                        </div>
                    </div>
                </div>
            </DataIndexTableColumnModalContainer>
        </Modal>

        {horizontalCRSModal && (
            <SearchableTable
                onClose={() => setHorizontalCRSModal(false)}
                title="Select Target EPSG"
                titleColor="#fff"
                titleFontSize="20px"
                setSelectedRow={setSelectedHCRS}
                    data={horizontalCRSList}
            />
        )}

        {verticalCRSModal && (
            <SearchableTable
                onClose={() => setVerticalCRSModal(false)}
                title="Select Target EPSG"
                titleColor="#fff"
                titleFontSize="20px"
                setSelectedRow={setSelectedVCRS}
                data={verticalCRSList}
            />
        )}
    </>
    )
}